import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { getMissingDealConfirmations } from '@/components/ui/verification/get-missing-deal-confirmations';
import HotKeyListener from '@/components/ux/hot-key-listener';
import useDeal from '@/hooks/use-deal';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import type { Status } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { ProgressRing } from '../../ui/progress-ring/progress-ring';
import {
  BlockDoneDealDialog,
  type BlockDoneDealReason,
} from './block-done-deal-dialog';
import { DealStatusBadge } from './deal-status-badge';

export const getStatusOptions = ({
  className,
}: {
  className: string;
}): Option<Status>[] => {
  return [
    {
      value: 'draft',
      label: 'Draft (Checkout)',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-orange-500"
        />
      ),
      hotkey: '0',
    },
    {
      value: 'new',
      label: 'New',
      icon: (
        <ProgressRing
          percentage={20}
          className={className}
          circleClassName="text-purple-500"
        />
      ),
      hotkey: '1',
    },
    {
      value: 'in_progress',
      label: 'In Progress',
      icon: (
        <ProgressRing
          percentage={30}
          className={className}
          circleClassName="text-blue-500"
        />
      ),
      hotkey: '2',
    },
    {
      value: 'awaiting_delivery',
      label: 'Awaiting Delivery',
      icon: (
        <ProgressRing
          percentage={60}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '3',
    },
    {
      value: 'delivered',
      label: 'Delivered',
      icon: (
        <ProgressRing
          percentage={80}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '4',
    },
    {
      value: 'done',
      label: 'Done',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '5',
    },
    {
      value: 'cancelled',
      label: 'Cancelled',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          // DEV NOTE COLOR BLIND UNFRIENDLY SPOT: this is a color blind unfriendly spot
          // this should be "text-red-500" when we have a non-color-blind users
          // one day we will support that, just noting it for now
          circleClassName="text-gray-500"
        />
      ),
      hotkey: '6',
    },
    {
      value: 'returning',
      label: 'Returning',
      icon: (
        <ProgressRing
          percentage={50}
          className={className}
          circleClassName="text-amber-500"
        />
      ),
      hotkey: '7',
    },
    {
      value: 'returned',
      label: 'Returned',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-gray-500"
        />
      ),
      hotkey: '8',
    },
  ];
};

export const STATUS_OPTIONS_MAP = getStatusOptions({ className: 'h-3 w-3' });

export type DealStatusSelectorProps = {
  id: string;
  shortId: string;
  value: Deal['status'];
  deal?: Deal;
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function DealStatusSelector({
  id,
  shortId,
  value,
  deal,
  hideCopy = false,
  side = 'left',
  // DEV UX NOTE: do not turn `withHotkeyListener` in any list or board views
  // because it will lag the page. This is purely meant for limited number of
  // DealStatusSelectors on a page.
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: DealStatusSelectorProps) {
  const { deal: storeDeal } = useDeal(shortId);

  const effectiveDeal = storeDeal ?? deal;

  const statusButtonRef = useRef<HTMLButtonElement>(null);

  const { isFeatureEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.DEAL_DRAFT_PROMOTION,
  });

  const { isFeatureEnabled: canReturnVehicle } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.RETURN_VEHICLE,
  });

  const { isFeatureEnabled: canOverrideDealStatus } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.OVERRIDE_DEAL_STATUS,
  });

  const [blockDoneDealDialogOpen, setBlockDoneDealDialogOpen] = useState(false);
  const [blockDoneDealReasons, setBlockDoneDealReasons] = useState<
    BlockDoneDealReason[]
  >([]);

  const updateStatusMutation = useMutation({
    onMutate: async ({ status }) => {
      dealStore.updateStatus({ id, status });
    },
    mutationFn: async ({
      status,
      override,
    }: {
      status: Deal['status'];
      override?: boolean;
    }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/status`, { status, override })
        .catch((e) => {
          console.error('Error updating status', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating status', error, variables);
    },
    onSettled: () => {},
  });

  const handleStatusChange = (newStatus: Deal['status']) => {
    if (effectiveDeal?.status == 'draft' && !isFeatureEnabled) {
      toast({
        title: 'You do not have permission to change the status of this deal',
        description: 'Please contact a manager to promote this deal.',
      });
      return;
    }

    if (effectiveDeal?.status === 'done' && newStatus !== 'returning') {
      if (!canOverrideDealStatus) {
        toast({
          title: 'Status cannot be changed',
          description:
            'Done deals can only be changed to "returning". For other changes, please use the "Reactivate Deal" button.',
        });
        return;
      }
    }

    if (effectiveDeal?.status === 'cancelled') {
      toast({
        title: 'Status cannot be changed',
        description:
          'This deal is in a non editable state. Please use the "Reactivate Deal" button to reactivate this deal.',
      });
      return;
    }

    if (effectiveDeal?.status === 'returning' && newStatus !== 'returned') {
      toast({
        title: 'Status cannot be changed',
        description: 'Returning deals can only be changed to "returned".',
      });
      return;
    }

    if (effectiveDeal?.status === 'returned') {
      toast({
        title: 'Status cannot be changed',
        description: 'This deal is in a non editable state.',
      });
      return;
    }

    if (newStatus === 'draft') {
      toast({
        title: 'Status cannot be moved to a draft state',
      });
      return;
    }

    if (newStatus === 'returning' && !canReturnVehicle) {
      toast({
        title: 'You do not have permission to return vehicles',
        description: 'Please contact a manager or engineer.',
      });
      return;
    }

    if (newStatus === 'returning' && effectiveDeal?.status !== 'done') {
      toast({
        title: 'Status cannot be changed to "returning"',
        description: 'Only "done" deals can be changed to "returning".',
      });
      return;
    }

    if (newStatus === 'returned' && effectiveDeal?.status !== 'returning') {
      toast({
        title: 'Status cannot be changed to "returned"',
        description: 'Only "returning" deals can be changed to "returned".',
      });
      return;
    }

    if (newStatus === 'done') {
      const reasons: BlockDoneDealReason[] = [];

      if (effectiveDeal?.status !== 'delivered') {
        reasons.push({
          key: 'DELIVERY_NOT_COMPLETED',
          title:
            'Deal must be in “Delivered” status before being marked "Done"',
        });
      }

      const hasPickupDate = !!effectiveDeal?.pickup_date;

      if (!hasPickupDate) {
        reasons.push({
          key: 'MISSING_PICKUP_DATE',
          title: 'Pickup date is required',
        });
      }

      const hasDeliveryDate = !!effectiveDeal?.delivery_date;

      if (!hasDeliveryDate) {
        reasons.push({
          key: 'MISSING_DELIVERY_DATE',
          title: 'Delivery date is required',
        });
      }

      if (effectiveDeal?.stage !== 'funded') {
        reasons.push({
          key: 'CONTRACT_STATUS_NOT_FUNDED',
          title: 'Contract status must be "Funded"',
        });
      }

      const owedPayments = (effectiveDeal?.payments ?? []).reduce(
        (acc, payment) => {
          if (payment.deleted_at) return acc;
          if (!payment.confirmed_date) return acc + payment.amount;
          return acc;
        },
        0,
      );

      if (owedPayments > 0) {
        reasons.push({
          key: 'PAYMENTS_NOT_COMPLETED',
          title: 'All payments must be completed',
        });
      }

      const incompleteTasks = (effectiveDeal?.tasks ?? []).filter(
        (task: any) => task.status !== 'done',
      );

      if (incompleteTasks.length > 0) {
        reasons.push({
          key: 'TASKS_NOT_COMPLETED',
          title: 'All tasks must be completed',
        });
      }

      const hasCoRegistrant = !!effectiveDeal?.co_registrant;

      const hasDeliveryAddress =
        !!effectiveDeal?.primary_customer?.addresses?.some(
          (address) => address.address_type === 'delivery',
        );

      const missingDealConfirmations = getMissingDealConfirmations({
        dealId: id,
        hasCoRegistrant: hasCoRegistrant,
        hasDeliveryAddress: hasDeliveryAddress,
      });

      if (missingDealConfirmations.length > 0) {
        reasons.push({
          key: 'VERIFICATIONS_NOT_COMPLETED',
          title: `All verifications must be completed`,
        });
      }

      if (reasons.length > 0) {
        setBlockDoneDealReasons(reasons);
        setBlockDoneDealDialogOpen(true);
        return;
      }
    }

    const shouldOverride =
      effectiveDeal?.status === 'done' &&
      newStatus !== 'returning' &&
      canOverrideDealStatus;

    updateStatusMutation.mutate({
      status: newStatus,
      override: shouldOverride || undefined,
    });
  };

  const currentOption = useMemo(() => {
    return STATUS_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_STATUS}</KeyboardKeyIcon>}
        placeholder="Set status..."
        currentOption={currentOption}
        onChange={handleStatusChange}
        emptyMessage="No status found"
        options={STATUS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = STATUS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleStatusChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div
        // DEV NOTE: without this div, combobox-popover won't work
        // that is, clicks will not open the combobox
        // I think the reason is children node needs to be able to receive a ref
        // in order to detect a click event, which makes it open
        // thus so this div is a workaround
        >
          <DealStatusBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.DEAL_STATUS}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
      <BlockDoneDealDialog
        open={blockDoneDealDialogOpen}
        onOpenChange={setBlockDoneDealDialogOpen}
        reasons={blockDoneDealReasons}
      />
    </div>
  );
}
